/**
 * Random Code Generator — gedeelde generator voor korte, voorleesbare codes.
 *
 * Gebruikt door de koppelcode (auth.ts) en de klas-/docentcode (classrooms.ts).
 * Zie `docs/project/SHORT_CODE_SECURITY.md` voor het volledige codestelsel.
 */

/**
 * Ondubbelzinnige tekens — 29 stuks.
 *
 * ─── WAAROM 29 EN NIET 32 ────────────────────────────────────────────────────
 *
 * Weggelaten: `I`, `O`, `0`, `1` (klassieke verwarring) én `L`, `S`, `5`.
 * Die laatste drie zijn er in aug 2026 bij gekomen op verzoek van de backend, en
 * de reden is niet visueel maar auditief: deze codes worden door een KIND
 * VOORGELEZEN aan een ouder, vaak door de telefoon. "L" en "S" klinken in het
 * Nederlands ("el" / "es") sterk op elkaar, en "S" en "5" ("es" / "vijf") worden
 * in gesproken vorm regelmatig verwisseld. Een code die niet over te brengen is,
 * faalt op precies het moment waarvoor hij bestaat.
 *
 * ─── DIT MOET GELIJK BLIJVEN MET DE BACKEND ──────────────────────────────────
 *
 * Zolang beide kanten codes genereren, moeten de alfabetten IDENTIEK zijn. Lopen
 * ze uiteen, dan is de entropieberekening waarop de ratelimiet is afgestemd niet
 * meer geldig — en dat merk je nergens aan, want beide kanten blijven gewoon
 * geldige codes maken. Wijk hier dus niet van af zonder de backend.
 *
 * Na de migratie hoort deze generator te VERDWIJNEN (SHORT_CODE_SECURITY.md F-6):
 * een dode generator naast een levende backend is een tweede definitie van de
 * entropie, en dat is precies hoe de twee alfabetten konden ontstaan.
 */
export const UNAMBIGUOUS_CHARSET = 'ABCDEFGHJKMNPQRTUVWXYZ2346789';

/**
 * Genereer een code van de gegeven lengte uit het ondubbelzinnige alfabet.
 * Gebruikt crypto.getRandomValues() voor onvoorspelbare uitvoer (FS-1, SEC-3).
 *
 * Let op: de modulo hieronder is licht vertekend (2^32 is geen veelvoud van 29).
 * Dat is hier aanvaardbaar omdat de scheefheid in de orde van 1e-8 ligt en de
 * echte bescherming van de pogingenlimiet komt, niet van perfecte uniformiteit.
 * In de backend, waar de code het enige geheim is, hoort een rejection-sampler.
 */
export function generateRandomCode(length: number = 6, charset: string = UNAMBIGUOUS_CHARSET): string {
  const randomValues = new Uint32Array(length);
  crypto.getRandomValues(randomValues);
  let code = '';
  for (let i = 0; i < length; i++) {
    code += charset.charAt(randomValues[i] % charset.length);
  }
  return code;
}
